import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const RegistrationForm = ({ onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: ''
  });

  const [errors, setErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const validatePassword = (password) => {
    const hasMinLength = password?.length >= 8;
    const hasUpperCase = /[A-Z]/?.test(password);
    const hasLowerCase = /[a-z]/?.test(password);
    const hasNumber = /\d/?.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/?.test(password);

    return {
      isValid: hasMinLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar,
      strength: [hasMinLength, hasUpperCase, hasLowerCase, hasNumber, hasSpecialChar]?.filter(Boolean)?.length
    };
  };

  const calculateAge = (dob) => {
    if (!dob) return 0;
    const [day, month, year] = dob?.split('/')?.map(Number);
    const birthDate = new Date(year, month - 1, day);
    const today = new Date();
    let age = today?.getFullYear() - birthDate?.getFullYear();
    const monthDiff = today?.getMonth() - birthDate?.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) {
      age--;
    }
    return age;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    if (field === 'email' && value && !validateEmail(value)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
    }

    if (field === 'password' && value) {
      const validation = validatePassword(value);
      if (!validation?.isValid) {
        setErrors(prev => ({ 
          ...prev, 
          password: 'Password must be at least 8 characters with uppercase, lowercase, number, and special character' 
        }));
      }
    }

    if (field === 'confirmPassword' && value && value !== formData?.password) {
      setErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
    }

    if (field === 'dateOfBirth' && value) {
      let age = calculateAge(value);
      if (age < 18) {
        setErrors(prev => ({ ...prev, dateOfBirth: 'You must be at least 18 years old' }));
      } else if (age > 100) {
        setErrors(prev => ({ ...prev, dateOfBirth: 'Please enter a valid date of birth' }));
      }
    }
  };

  const handleDateInput = (e) => {
    let value = e?.target?.value?.replace(/\D/g, '');
    
    if (value?.length >= 2) {
      value = value?.slice(0, 2) + '/' + value?.slice(2);
    }
    if (value?.length >= 5) {
      value = value?.slice(0, 5) + '/' + value?.slice(5, 9);
    }
    
    handleInputChange('dateOfBirth', value);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (!validatePassword(formData?.password)?.isValid) {
      newErrors.password = 'Password does not meet requirements';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData?.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      let age = calculateAge(formData?.dateOfBirth);
      if (age < 18) {
        newErrors.dateOfBirth = 'You must be at least 18 years old';
      }
    }

    if (!termsAccepted) {
      newErrors.terms = 'You must accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const passwordValidation = validatePassword(formData?.password);
  const strengthColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];
  const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Input
        label="Full Name"
        type="text"
        placeholder="Enter your full name"
        value={formData?.fullName}
        onChange={(e) => handleInputChange('fullName', e?.target?.value)}
        error={errors?.fullName}
        required
      />
      <Input
        label="Email Address"
        type="email"
        placeholder="your.email@example.com"
        value={formData?.email}
        onChange={(e) => handleInputChange('email', e?.target?.value)}
        error={errors?.email}
        description="We'll use this for account verification and updates"
        required
      />
      <div className="space-y-2">
        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Create a strong password"
            value={formData?.password}
            onChange={(e) => handleInputChange('password', e?.target?.value)}
            error={errors?.password}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>
        
        {formData?.password && (
          <div className="space-y-2">
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5]?.map((level) => (
                <div
                  key={level}
                  className={`h-1 flex-1 rounded-full transition-all ${
                    level <= passwordValidation?.strength
                      ? strengthColors?.[passwordValidation?.strength - 1]
                      : 'bg-muted'
                  }`}
                />
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Password strength: {strengthLabels?.[passwordValidation?.strength - 1] || 'Very Weak'}
            </p>
          </div>
        )}
      </div>
      <div className="relative">
        <Input
          label="Confirm Password"
          type={showConfirmPassword ? 'text' : 'password'}
          placeholder="Re-enter your password"
          value={formData?.confirmPassword}
          onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
          error={errors?.confirmPassword}
          required
        />
        <button
          type="button"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
        >
          <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
        </button>
      </div>
      <Input
        label="Date of Birth"
        type="text"
        placeholder="DD/MM/YYYY"
        value={formData?.dateOfBirth}
        onChange={handleDateInput}
        error={errors?.dateOfBirth}
        description="Used to calculate your retirement timeline"
        maxLength={10}
        required
      />
      <div className="space-y-4">
        <Checkbox
          label="I accept the Terms and Conditions and Privacy Policy"
          checked={termsAccepted}
          onChange={(e) => {
            setTermsAccepted(e?.target?.checked);
            if (errors?.terms) {
              setErrors(prev => ({ ...prev, terms: '' }));
            }
          }}
          error={errors?.terms}
          required
        />
        
        <div className="flex gap-2 text-sm text-muted-foreground">
          <button
            type="button"
            className="text-primary hover:underline focus-ring rounded"
            onClick={() => window.open('/terms', '_blank')}
          >
            Terms and Conditions
          </button>
          <span>•</span>
          <button
            type="button"
            className="text-primary hover:underline focus-ring rounded"
            onClick={() => window.open('/privacy', '_blank')}
          >
            Privacy Policy
          </button>
        </div>
      </div>
      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isLoading}
        iconName="UserPlus"
        iconPosition="left"
      >
        Create Account
      </Button>
    </form>
  );
};

export default RegistrationForm;